import React, { useState, useEffect, useMemo } from "react";
import ActionButton from "./ActionButton";
import {
  useGetCampaignsQuery,
  useCloneCampaignMutation,
  useUpdateCampaignStatusMutation,
} from "../../RTKQuery/CampaignService/campaignApi";
import Loader from "../Loader/Loader";
import PageComponentHeader from "../PageComponentHeader";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  Chip,
  Divider,
  Tooltip,
  Collapse,
} from "@mui/material";
import CampaignStatusChip from "../UIKit/Chip/CampaignStatusChip";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { useNavigate } from "react-router-dom";
import Upload from "../Modals/Upload/Upload";
import MuiSnackbar from "../UIKit/Snackbar";
import { dayFormat, customDateFormat } from "../../utils/helperFunctions";
import {
  searchField,
  searchInput,
  buttonsDiv,
} from "../../pages/Dashboard/styles";
import "./style.css";
import { sourceColors, defaultSourceColors, sourceChipBase } from "./styles";
import { useSelector } from "react-redux";
import DataTable from "../UIKit/CollapsibleTable";
import CampaignNameGenerationModal from "../Modals/CampaignName";
import SearchIcon from "@mui/icons-material/Search";
import PrimaryButton from "../UIKit/Buttons/Primary";
import ConfirmationDialog from "../Modals/ConfirmationModal/ConfirmationDialog";
// import RefreshButton from "../UIKit/Buttons/RefreshStats";

const CAMPAIGNS_SELECT_COLUMN_ID = "mrt-row-select";
const CAMPAIGNS_ACTIONS_COLUMN_ID = "actions";

export default function Campaigns() {
  const CAMPAIGNS_COLUMN_ORDER_STORAGE_KEY = "campaigns-table-column-order";
  const [currentPage, setCurrentPage] = useState(0);
  const files = useSelector((state) => state.file.files);

  // upload props
  const navigate = useNavigate();
  const [uploadModal, setUploadModal] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [rows, setRows] = useState(10);
  //Alert props
  const [showAlert, setShowAlert] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("error");

  //Table Props
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [sorting, setSorting] = useState([]);
  const [loader, setLoader] = useState(true);

  //Search Props
  const [search, setSearch] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Row Selection Props
  const [rowSelection, setRowSelection] = useState({});

  // Confirmation Modal States
  const [cloneConfirmOpen, setCloneConfirmOpen] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [selectedCampaignTitle, setSelectedCampaignTitle] = useState("");

  const handleClick = (id) => {
    navigate(`/campaign/details/${id}`);
    // console.log(id);
  };

  // Get selected campaign IDs
  const selectedCampaignIds = Object.keys(rowSelection).filter(
    (key) => rowSelection[key]
  );

  // Handle view grouped campaigns
  const handleViewGroupedCampaigns = () => {
    if (selectedCampaignIds.length > 0) {
      // Navigate to grouped campaigns page with IDs as query params
      const idsParam = selectedCampaignIds.join(",");
      navigate(`/campaign/details/grouped?ids=${idsParam}`);
    }
  };

  // Clear selection
  const handleClearSelection = () => {
    setRowSelection({});
  };

  // Action handlers - Open confirmation modals
  const handleCloneCampaignClick = (campaignId, campaignTitle, e) => {
    e.stopPropagation();
    setSelectedCampaignId(campaignId);
    setSelectedCampaignTitle(campaignTitle);
    setCloneConfirmOpen(true);
  };

  const handleChangeStatusClick = (campaignId, campaignTitle, e) => {
    e.stopPropagation();
    setSelectedCampaignId(campaignId);
    setSelectedCampaignTitle(campaignTitle);
    setStatusConfirmOpen(true);
  };

  // Confirmed actions
  const handleCloneCampaign = async () => {
    try {
      await cloneCampaign(selectedCampaignId).unwrap();
      setMessage("Campaign cloned successfully");
      setSeverity("success");
      setShowAlert(true);
      setCloneConfirmOpen(false);
      setSelectedCampaignId(null);
      setSelectedCampaignTitle("");
      refetch();
    } catch (error) {
      setMessage(error?.data?.message || "Failed to clone campaign");
      setSeverity("error");
      setShowAlert(true);
      setCloneConfirmOpen(false);
    }
  };

  const handleChangeStatusToNeverSent = async () => {
    try {
      await updateCampaignStatus({
        campaignId: selectedCampaignId,
        status: "Never sent",
      }).unwrap();
      setMessage("Campaign status updated to 'Never Sent'");
      setSeverity("success");
      setShowAlert(true);
      setStatusConfirmOpen(false);
      setSelectedCampaignId(null);
      setSelectedCampaignTitle("");
      refetch();
    } catch (error) {
      setMessage(error?.data?.message || "Failed to update campaign status");
      setSeverity("error");
      setShowAlert(true);
      setStatusConfirmOpen(false);
    }
  };

  const handleResumeStartListGeneration = (campaignId, e) => {
    e.stopPropagation();
    navigate(`/prospects?campaignId=${campaignId}`);
  };

  const {
    data,
    isLoading: loading,
    refetch,
  } = useGetCampaignsQuery({
    pagination,
    sorting,
    searchQuery,
  });

  const [cloneCampaign] = useCloneCampaignMutation();
  const [updateCampaignStatus] = useUpdateCampaignStatusMutation();

  const CampaignsColumns = [
    // {
    //   accessorKey: " ",
    //   header: " ",
    //   enableSorting: false,
    //   size: 30,
    //   Cell: ({ row }) => <ActionButton id={row.original.id} />,
    // },
    {
      accessorKey: "campaignTitle",
      header: "Title",
      enableSorting: false,
      size: 200,
      Cell: ({ cell }) => (
        <div
          className="title-div"
          onClick={(e) => {
            e.stopPropagation(); // Prevent row selection when clicking title
            handleClick(cell.row.original.id);
          }}
          style={{ cursor: "pointer" }}
        >
          {/* <ActionButton id={cell.row.original.id} /> */}
          {cell?.getValue()}
        </div>
      ),
    },
    {
      accessorKey: "campaignSubject",
      header: "Subject",
      enableSorting: false,
      size: 200,
      Cell: ({ cell }) => (cell?.getValue() === "" ? "N/A" : cell?.getValue()),
    },
    {
      accessorKey: "createdAt",
      header: "Created At",
      enableSorting: false,
      size: 120,
      Cell: ({ cell }) => {
        const value = cell?.getValue();
        return value ? customDateFormat(value, "MM-DD-YYYY") : "N/A";
      },
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
    },
    {
      accessorKey: "campaignSource",
      header: "Campaign Source",
      enableSorting: false,
      size: 50,
      Cell: ({ cell }) => {
        const value = cell?.getValue();

        if (!value || value === "" || value === null) {
          return "N/A";
        }

        const colors = sourceColors[value] || defaultSourceColors;

        return (
          <Chip
            label={value}
            size="small"
            sx={{
              ...colors,
              ...sourceChipBase,
            }}
          />
        );
      },
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
    },
    {
      accessorKey: "followUpNumber",
      header: "Campaign Steps",
      enableSorting: false,
      size: 40,
      Cell: ({ cell }) => {
        const row = cell.row.original;
        const followUpNumber = row?.followUpNumber;
        return <Chip label={followUpNumber} size="small" />;
      },
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      enableSorting: false,
      size: 150,
      Cell: ({ cell }) => {
        const status = cell?.getValue();
        return <CampaignStatusChip status={status} size="small" />;
      },
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
    },
    {
      accessorKey: "totalProcessed",
      header: "Processed",
      enableSorting: true,
      size: 40,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
    },
    {
      accessorKey: "totalSent",
      header: "Delivered",
      enableSorting: true,
      size: 40,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
    },
    {
      accessorKey: "totalOpen",
      header: "Opened",
      enableSorting: true,
      size: 40,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
    },
    {
      accessorKey: "clicks",
      header: "Clicked",
      enableSorting: true,
      size: 40,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
    },
    {
      accessorKey: "bounced",
      header: "Bounced",
      enableSorting: true,
      size: 40,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
    },
    // {
    //   accessorKey: "blocked",
    //   header: "Blocked",
    //   enableSorting: true,
    //   size: 40,
    // },
    {
      accessorKey: "unsubScribed",
      header: "Unsubscribed",
      enableSorting: true,
      size: 40,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
    },
    {
      accessorKey: "openRate",
      header: "Open Rate",
      enableSorting: true,
      size: 40,
      Cell: ({ cell }) => cell?.getValue().toFixed(1) + "%",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
    },
    {
      accessorKey: "clickRate",
      header: "Click Rate",
      enableSorting: true,
      size: 40,
      Cell: ({ cell }) => cell?.getValue().toFixed(1) + "%",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      enableColumnOrdering: false,
      size: 150,
      enablePinning: true,
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: "#367598 !important",
          color: "#fff !important",
          fontWeight: "bold",
          textAlign: "center !important",
          padding: "8px",
          "& .Mui-TableSortLabel-root": {
            color: "#fff !important",
          },
          "& .MuiTableSortLabel-root": {
            color: "#fff !important",
          },
          "& .MuiTableSortLabel-root.Mui-active": {
            color: "#fff !important",
          },
          "& .MuiIconButton-root": {
            color: "#fff !important",
          },
        },
        align: "center",
      },
      Cell: ({ row }) => {
        const status = row.original.status?.toLowerCase();
        const showStatusChange =
          status !== "active" && status !== "closed" && status !== "never sent";
        const showResume =
          status !== "exported" &&
          status !== "active" &&
          status !== "closed" &&
          status !== "never sent";

        return (
          <Box
            sx={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <Tooltip title="Clone Campaign" arrow>
              <IconButton
                size="small"
                onClick={(e) =>
                  handleCloneCampaignClick(
                    row.original.id,
                    row.original.campaignTitle,
                    e
                  )
                }
                sx={{
                  color: "#367598",
                  "&:hover": {
                    backgroundColor: "#e3f2fd",
                  },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {showResume && (
              <Tooltip title="Generate List" arrow>
                <IconButton
                  size="small"
                  onClick={(e) =>
                    handleResumeStartListGeneration(row.original.id, e)
                  }
                  sx={{
                    color: "#10b981",
                    "&:hover": {
                      backgroundColor: "#d1fae5",
                    },
                  }}
                >
                  <ListAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {showStatusChange && (
              <Tooltip title="Change Status to 'Never Sent'" arrow>
                <IconButton
                  size="small"
                  onClick={(e) =>
                    handleChangeStatusClick(
                      row.original.id,
                      row.original.campaignTitle,
                      e
                    )
                  }
                  sx={{
                    color: "#d32f2f",
                    "&:hover": {
                      backgroundColor: "#f3f4f6",
                    },
                  }}
                >
                  <HighlightOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const defaultCampaignsColumnOrder = useMemo(() => {
    const dataColumns = CampaignsColumns.map(
      (column) => column.id || column.accessorKey
    ).filter(Boolean);

    const middleColumns = dataColumns.filter(
      (columnId) =>
        columnId !== CAMPAIGNS_SELECT_COLUMN_ID &&
        columnId !== CAMPAIGNS_ACTIONS_COLUMN_ID
    );

    return [
      CAMPAIGNS_SELECT_COLUMN_ID,
      ...middleColumns,
      CAMPAIGNS_ACTIONS_COLUMN_ID,
    ];
  }, [CampaignsColumns]);

  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const storedValue = localStorage.getItem(
        CAMPAIGNS_COLUMN_ORDER_STORAGE_KEY
      );
      const parsedValue = storedValue ? JSON.parse(storedValue) : [];
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch (error) {
      return [];
    }
  });

  useEffect(() => {
    if (!defaultCampaignsColumnOrder.length) return;

    setColumnOrder((previousOrder) => {
      const allowedIds = new Set(defaultCampaignsColumnOrder);
      const safePreviousOrder = Array.isArray(previousOrder)
        ? previousOrder.filter((columnId) => allowedIds.has(columnId))
        : [];

      const middleFromSaved = safePreviousOrder.filter(
        (columnId) =>
          columnId !== CAMPAIGNS_SELECT_COLUMN_ID &&
          columnId !== CAMPAIGNS_ACTIONS_COLUMN_ID
      );
      const missingMiddle = defaultCampaignsColumnOrder.filter(
        (columnId) =>
          columnId !== CAMPAIGNS_SELECT_COLUMN_ID &&
          columnId !== CAMPAIGNS_ACTIONS_COLUMN_ID &&
          !middleFromSaved.includes(columnId)
      );

      const nextOrder = [
        CAMPAIGNS_SELECT_COLUMN_ID,
        ...middleFromSaved,
        ...missingMiddle,
        CAMPAIGNS_ACTIONS_COLUMN_ID,
      ];

      const isSameOrder =
        nextOrder.length === previousOrder.length &&
        nextOrder.every((columnId, index) => columnId === previousOrder[index]);

      return isSameOrder ? previousOrder : nextOrder;
    });
  }, [defaultCampaignsColumnOrder]);

  useEffect(() => {
    if (!columnOrder.length) return;
    localStorage.setItem(
      CAMPAIGNS_COLUMN_ORDER_STORAGE_KEY,
      JSON.stringify(columnOrder)
    );
  }, [columnOrder]);

  const handleUploadModal = (bool) => {
    setShowMap(bool);
    setUploadModal(true);
  };

  // Debounce Effect: Wait until user stops typing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchQuery(inputValue); // Update searchQuery after 500ms
    }, 500); // 500ms delay

    return () => clearTimeout(delayDebounce); // Cleanup on input change
  }, [inputValue]); // Runs when inputValue changes

  // Update temporary input state on typing
  const handleSearchChange = (e) => {
    setInputValue(e.target.value);
  };

  // Force search when clicking the search button
  const handleSearch = () => {
    setSearchQuery(inputValue);
  };

  useEffect(() => {
    refetch();
  }, [data, files]);

  // console.log(data);

  return (
    <>
      <PageComponentHeader
        pageTitle={"Campaigns"}
        titleHelperText={
          data &&
          `Updated: ${dayFormat(data?.lastUpdatedAt, "DD/MM/YYYY hh:mm:ss A")}`
        }
      >
        <Box sx={searchField}>
          <input
            type="text"
            style={searchInput}
            className="inputSearch"
            placeholder="Search by Campaign Title..."
            onChange={handleSearchChange}
          />
          <IconButton onClick={handleSearch}>
            {/* <img src="Research.png" alt="" /> */}
            <SearchIcon style={{ color: "#367598" }} />
          </IconButton>
        </Box>
        <Box sx={buttonsDiv}>
          <CampaignNameGenerationModal />
          <Button
            startIcon={<UploadFileIcon />}
            color="primary"
            variant="contained"
            sx={{ color: "#F3F3F3" }}
            onClick={() => handleUploadModal(false)}
          >
            Upload Stats
          </Button>
          {/* <RefreshButton/> */}
        </Box>
      </PageComponentHeader>
      <Upload
        sendGrid={true}
        open={uploadModal}
        setOpen={setUploadModal}
        showMap={showMap}
        setShowMap={setShowMap}
        uploading={uploading}
        setUploading={setUploading}
        setShowAlert={setShowAlert}
        setSeverity={setSeverity}
        setMessage={setMessage}
      />
      <MuiSnackbar
        show={showAlert}
        setShow={setShowAlert}
        severity={severity}
        message={message}
      />
      <ConfirmationDialog
        open={cloneConfirmOpen}
        onClose={() => {
          setCloneConfirmOpen(false);
          setSelectedCampaignId(null);
          setSelectedCampaignTitle("");
        }}
        onConfirm={handleCloneCampaign}
        title="Clone Campaign"
        message={`Are you sure you want to clone the campaign "${selectedCampaignTitle}"? This will create a duplicate of the campaign.`}
        confirmText="Clone"
        cancelText="Cancel"
        confirmColor="primary"
        icon="clone"
      />
      <ConfirmationDialog
        open={statusConfirmOpen}
        onClose={() => {
          setStatusConfirmOpen(false);
          setSelectedCampaignId(null);
          setSelectedCampaignTitle("");
        }}
        onConfirm={handleChangeStatusToNeverSent}
        title="Change Campaign Status"
        message={`Are you sure you want to change the status of "${selectedCampaignTitle}" to "Never Sent"?`}
        confirmText="Change Status"
        cancelText="Cancel"
        confirmColor="danger"
        icon="status"
      />
      {loading ? (
        <div className="noData">
          <Loader />
        </div>
      ) : data?.campaigns.length > 0 ? (
        <div style={{ marginTop: "1rem" }}>
          {/* Selection Toolbar - appears when campaigns are selected */}
          <Collapse in={selectedCampaignIds.length > 0} timeout={400}>
            <Paper
              elevation={0}
              sx={{
                padding: "0.5rem 1rem",
                marginBottom: "1rem",
                backgroundColor: "#ffffff",
                border: "2px solid #367598",
                borderRadius: "5px",
                boxShadow: "0 0 0 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "1.5rem",
                flexWrap: "wrap",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(54, 117, 152, 0.2)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1.25rem",
                  flex: 1,
                  minWidth: "200px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "30px",
                    height: "30px",
                    borderRadius: "50%",
                    backgroundColor: "#e3f2fd",
                    color: "#367598",
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: "20px" }} />
                </Box>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    {selectedCampaignIds.length} Campaign
                    {selectedCampaignIds.length > 1 ? "s" : ""} Selected
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      fontSize: "12px",
                    }}
                  >
                    Click "View Grouped Details" to see combined statistics
                  </Typography>
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem />

              <Box
                sx={{
                  display: "flex",
                  gap: "0.75rem",
                  alignItems: "center",
                }}
              >
                <Tooltip
                  title="View combined statistics for selected campaigns"
                  arrow
                >
                  <PrimaryButton
                    onClick={handleViewGroupedCampaigns}
                    text="View Grouped Details"
                    icon={<AnalyticsIcon />}
                  />
                </Tooltip>
                <Tooltip title="Clear all selections" arrow>
                  <IconButton
                    onClick={handleClearSelection}
                    size="medium"
                    sx={{
                      color: "#666",
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #e0e0e0",
                      "&:hover": {
                        backgroundColor: "#ffebee",
                        borderColor: "#ef5350",
                        color: "#ef5350",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          </Collapse>

          <DataTable
            columns={CampaignsColumns}
            data={data.campaigns}
            totalData={data.total}
            sorting={sorting}
            setSorting={setSorting}
            pagination={pagination}
            setLoader={setLoader}
            setPagination={setPagination}
            enableCollapsibleRow={false}
            selectable={true}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            columnPinning={{
              left: [CAMPAIGNS_SELECT_COLUMN_ID],
              right: [CAMPAIGNS_ACTIONS_COLUMN_ID],
            }}
            columnOrder={columnOrder}
            setColumnOrder={setColumnOrder}
          />
        </div>
      ) : (
        <h3 className="noData">No Record Found</h3>
      )}
    </>
  );
}
